import isFunction from 'lodash/isFunction'
import join from 'lodash/join'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { formatPhoneNumber } from 'react-phone-number-input'
import { toast } from 'react-toastify'

import zentakeApi from '../../api'
import Ui from '../../ui'
import Icons from '../../ui/Icons'
import { buildPhoneNumberValue } from '../../ui/SingleLinePhone'
import { handleServerSearch, hasText } from '../../utils/helpers'
import { ZentakeContext } from '../../utils/ZentakeContext'
import useMergeClientState from './useMergeClientState'

function MergeClients({ handleOut, baseClientMaster, onMergeCallback }) {
  const { searchClients, fetchingClients } = useContext(ZentakeContext)

  const [isSubmitting, setSubmitting] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const {
    clientMaster,
    setClientMaster,
    listForClientMaster,
    clientToMerge,
    setClientToMerge,
    listForClientsToMerge,
    hasFormError,
    formError,
    formErrorLabel,
    validateForm,
  } = useMergeClientState(baseClientMaster)

  const performHandleSearch = useCallback(async () => {
    await searchClients(
      handleServerSearch({
        searchTerm,
        paginated: false,
      })
    )
    // eslint-disable-next-line
  }, [searchTerm])

  useEffect(() => {
    performHandleSearch()
    // eslint-disable-next-line
  }, [searchTerm])

  const onMergeClick = useCallback(async () => {
    setSubmitting(true)
    let success = false
    try {
      await validateForm()
      await zentakeApi.mergeClients(clientMaster.id, [clientToMerge.id])
      isFunction(onMergeCallback) && onMergeCallback()
      toast.success('Merge complete')
      success = true
    } catch (error) {
    } finally {
      setSubmitting(false)
      success && handleOut()
    }
  }, [validateForm, clientMaster, clientToMerge, handleOut, onMergeCallback])

  const LabelRenderer = useCallback(({ data }) => {
    const detailsArray = []
    // !isNil(data?.id) && detailsArray.push(data.id)
    hasText(data?.email) && detailsArray.push(data.email)
    hasText(data?.phone_number) &&
      detailsArray.push(
        formatPhoneNumber(buildPhoneNumberValue(data.phone_number))
      )
    hasText(data?.date_birth_us) && detailsArray.push(data.date_birth_us)
    const detailsText = join(detailsArray, ', ')
    return (
      <div>
        <p className="text-sm font-medium text-txt-emphasis">
          {`${data.first_name} ${data.last_name} (${data.id})`}
        </p>
        <p className="text-xs leading-4 text-txt">{detailsText}</p>
      </div>
    )
  }, [])

  return (
    <Ui.ModalDialog opened onClose={handleOut} title="Merge Clients">
      <p className="mt-1 text-sm leading-5 text-gray-500">
        Select the clients to merge together, the first one will be preserved
        and the second will be deleted and their packets will be merged.
      </p>
      <form>
        <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
          <div className="sm:col-span-3" style={{ minHeight: '4vh' }}>
            <Ui.Select
              showOptions
              LabelRenderer={LabelRenderer}
              disabled={baseClientMaster?.id !== undefined}
              errorLabel={formErrorLabel?.clientMaster}
              hasError={formError?.clientMaster}
              label="Master client"
              loading={fetchingClients}
              onChange={setClientMaster}
              onType={(value) => {
                setSearchTerm(value)
              }}
              options={listForClientMaster || []}
              placeholder="Master client"
              selectedLabelFormatter={(data) =>
                `${data.first_name} ${data.last_name}`
              }
              target="full_name"
              value={clientMaster}
              valueProp="id"
            />
            {/* <ClientDetails client={clientMaster} /> */}
          </div>
          <div className="sm:col-span-3" style={{ minHeight: '4vh' }}>
            <Ui.Select
              showOptions
              LabelRenderer={LabelRenderer}
              errorLabel={formErrorLabel?.clientToMerge}
              hasError={formError?.clientToMerge}
              label="Client to merge"
              loading={fetchingClients}
              onChange={setClientToMerge}
              onType={(value) => {
                setSearchTerm(value)
              }}
              options={listForClientsToMerge || []}
              placeholder="Client to merge"
              selectedLabelFormatter={(data) =>
                `${data.first_name} ${data.last_name}`
              }
              target="full_name"
              value={clientToMerge}
              valueProp="id"
            />
            {/* <ClientDetails client={clientToMerge} /> */}
          </div>
        </div>
        <div className="mt-5 flex w-full justify-end">
          <Ui.PrimaryButton
            className="space-x-2"
            disabled={hasFormError || isSubmitting}
            loading={isSubmitting}
            onClick={onMergeClick}
          >
            {!isSubmitting && <Icons.Merge className="text-white" />}
            <span>Merge</span>
          </Ui.PrimaryButton>
        </div>
      </form>
    </Ui.ModalDialog>
  )
}

export default MergeClients
